import { AfterViewInit, Component, ElementRef, EventEmitter, forwardRef, OnDestroy, Output, ViewChild } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { Subject, takeUntil } from "rxjs";
import intlTelInput, { Iti } from "intl-tel-input";
import { I18n } from "intl-tel-input/i18n/types";

// Eager load English and Dutch (most common languages)
import en from "intl-tel-input/i18n/en";
import nl from "intl-tel-input/i18n/nl";

import { CredentialService } from "src/app/credential.service";
import { Language } from "src/app/enums/language.enum";

@Component({
  standalone: false,
  selector: "app-phone-number-input",
  templateUrl: "./phone-number-input.component.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneNumberInputComponent),
      multi: true
    }
  ]
})
export class PhoneNumberInputComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  @ViewChild("phonenumberInput", { static: false }) phoneNumberInput: ElementRef<HTMLInputElement>;

  @Output() validityChange = new EventEmitter<boolean>();

  public isPhoneNumberInvalid = false;
  private contactNumberInput: Iti;
  private _value: string;
  private readonly destroy$ = new Subject<void>();

  // Eagerly loaded translations
  private readonly eagerI18nMap: Partial<Record<Language, I18n>> = {
    [Language.English]: en,
    [Language.Dutch]: nl
  };

  // Cache for lazy-loaded translations
  private readonly lazyI18nCache: Partial<Record<Language, I18n>> = {};

  // =============================================================================
  // DROPDOWN POSITIONING - Event handlers and cached references
  // =============================================================================

  /** Estimated dropdown height: country list (185px) + search input (~50px) + padding */
  private static readonly DROPDOWN_HEIGHT_ESTIMATE = 250;

  /** Cached reference to the ITI container element to avoid repeated DOM queries */
  private itiContainer: HTMLElement | null = null;

  /** Bound event handler references for proper cleanup */
  private readonly boundDropdownOpenHandler = this.handleDropdownOpen.bind(this);
  private readonly boundDropdownCloseHandler = this.handleDropdownClose.bind(this);
  private readonly boundCountryChangeHandler = this.onPhoneNumberChange.bind(this);
  private readonly boundResizeHandler = this.handleWindowResize.bind(this);

  /** Flag to track if dropdown is currently open (for resize handling) */
  private isDropdownOpen = false;

  onChange = (_: any) => {
    //
  };
  onTouched = () => {};

  constructor(
    private readonly credentialService: CredentialService,
    private readonly translateService: TranslateService
  ) {}

  ngAfterViewInit(): void {
    this.initializeWithI18n();
    this.subscribeToLanguageChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cleanupEventListeners();
    this.cleanupResizeListener();
    if (this.contactNumberInput) {
      this.contactNumberInput.destroy();
    }
    this.itiContainer = null;
  }

  writeValue(value: any): void {
    this._value = value;
    if (this.contactNumberInput) {
      this.contactNumberInput.setNumber(value || "");
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /**
   * Initialize the phone input with i18n support.
   * Uses eager-loaded translations for English/Dutch, lazy-loads French/German.
   */
  private async initializeWithI18n(): Promise<void> {
    const i18n = await this.getI18nForCurrentLanguage();
    this.initialiseContactNumberInput(i18n);
  }

  /**
   * Subscribe to language changes and reinitialize the component.
   */
  private subscribeToLanguageChanges(): void {
    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(async event => {
      try {
        // Use the language from the event directly, as localStorage may not be updated yet
        await this.reinitializeWithNewLanguage(event.lang as Language);
      } catch (error) {
        console.error(":::PhoneNumberInputComponent:::subscribeToLanguageChanges:::error:::", error);
      }
    });
  }

  /**
   * Reinitialize the phone input when language changes.
   * Preserves the current phone number value.
   * @param newLanguage - The new language to use (from onLangChange event)
   */
  private async reinitializeWithNewLanguage(newLanguage: Language): Promise<void> {
    // Store current value before destroying
    const currentValue = this._value;

    // Clean up existing event listeners before destroying to prevent memory leaks
    this.cleanupEventListeners();
    this.itiContainer = null;

    // Destroy existing instance
    if (this.contactNumberInput) {
      this.contactNumberInput.destroy();
    }

    // Reinitialize with new language - use the provided language directly
    const i18n = await this.getI18nForLanguage(newLanguage);
    this.initialiseContactNumberInput(i18n);

    // Restore the value
    if (currentValue) {
      this.contactNumberInput.promise
        .then(() => {
          this.contactNumberInput.setNumber(currentValue);
        })
        .catch(error => {
          console.error(":::PhoneNumberInputComponent:::reinitializeWithNewLanguage:::error:::", error);
        });
    }
  }

  /**
   * Get the i18n translations for the current language.
   * English and Dutch are eagerly loaded, French and German are lazy-loaded.
   */
  private async getI18nForCurrentLanguage(): Promise<I18n> {
    const language = this.getCurrentLanguage();
    return this.getI18nForLanguage(language);
  }

  /**
   * Get the i18n translations for a specific language.
   * English and Dutch are eagerly loaded, French and German are lazy-loaded.
   * @param language - The language to get translations for
   */
  private async getI18nForLanguage(language: Language): Promise<I18n> {
    // Check eagerly loaded translations first
    if (this.eagerI18nMap[language]) {
      return this.eagerI18nMap[language];
    }

    // Check lazy-loaded cache
    if (this.lazyI18nCache[language]) {
      return this.lazyI18nCache[language];
    }

    // Lazy load French or German
    try {
      let i18n: I18n;
      switch (language) {
        case Language.French:
          i18n = (await import("intl-tel-input/i18n/fr")).default;
          break;
        case Language.German:
          i18n = (await import("intl-tel-input/i18n/de")).default;
          break;
        default:
          // Fallback to English for unknown languages
          return this.eagerI18nMap[Language.English];
      }
      // Cache the loaded translation
      this.lazyI18nCache[language] = i18n;
      return i18n;
    } catch (error) {
      console.error(":::PhoneNumberInputComponent:::getI18nForLanguage:::error:::", error);
      // Fallback to English on error
      return this.eagerI18nMap[Language.English];
    }
  }

  /**
   * Get the current language from user preferences or TranslateService.
   */
  private getCurrentLanguage(): Language {
    // Try to get from user preferences first
    const user = this.credentialService.getUser();
    if (user?.preferredLanguage) {
      return user.preferredLanguage as Language;
    }
    // Fallback to TranslateService current language
    return (this.translateService.currentLang as Language) || Language.English;
  }

  private initialiseContactNumberInput(i18n: I18n): void {
    const inputElement = this.phoneNumberInput?.nativeElement;
    if (!inputElement) {
      console.error(":::PhoneNumberInputComponent:::initialiseContactNumberInput:::error::: Input element not found");
      return;
    }

    this.contactNumberInput = intlTelInput(inputElement, {
      nationalMode: true,
      separateDialCode: true,
      formatOnDisplay: true,
      placeholderNumberType: "MOBILE",
      initialCountry: "be",
      countryOrder: ["be"],
      strictMode: true,
      fixDropdownWidth: true,
      i18n,
      loadUtils: () => import("intl-tel-input/utils")
    });

    if (this._value) {
      // Wait for utils to load before setting number to ensure proper formatting
      this.contactNumberInput.promise
        .then(() => {
          this.contactNumberInput.setNumber(this._value);
        })
        .catch(error => {
          console.error(":::PhoneNumberInputComponent:::initialiseContactNumberInput:::error:::", error);
        });
    }

    // Cache the ITI container reference to avoid repeated DOM queries
    this.itiContainer = inputElement.closest(".iti");

    // Add event listeners using bound handlers for proper cleanup
    this.setupEventListeners(inputElement);
    this.setupResizeListener();
  }

  // =============================================================================
  // EVENT LISTENER MANAGEMENT
  // =============================================================================

  /**
   * Set up all event listeners for the phone input.
   * Uses bound handlers to ensure proper cleanup on destroy.
   */
  private setupEventListeners(inputElement: HTMLInputElement): void {
    inputElement.addEventListener("countrychange", this.boundCountryChangeHandler);
    inputElement.addEventListener("open:countrydropdown", this.boundDropdownOpenHandler);
    inputElement.addEventListener("close:countrydropdown", this.boundDropdownCloseHandler);
  }

  /**
   * Remove all event listeners to prevent memory leaks.
   * Called on component destroy and before reinitialization.
   */
  private cleanupEventListeners(): void {
    const inputElement = this.phoneNumberInput?.nativeElement;
    if (!inputElement) return;

    inputElement.removeEventListener("countrychange", this.boundCountryChangeHandler);
    inputElement.removeEventListener("open:countrydropdown", this.boundDropdownOpenHandler);
    inputElement.removeEventListener("close:countrydropdown", this.boundDropdownCloseHandler);
  }

  /**
   * Set up window resize listener to reposition dropdown if open during resize.
   */
  private setupResizeListener(): void {
    window.addEventListener("resize", this.boundResizeHandler);
  }

  /**
   * Remove window resize listener.
   */
  private cleanupResizeListener(): void {
    window.removeEventListener("resize", this.boundResizeHandler);
  }

  // =============================================================================
  // DROPDOWN POSITIONING LOGIC
  // =============================================================================

  /**
   * Handle dropdown open event.
   * Calculates and applies the optimal dropdown position.
   */
  private handleDropdownOpen(): void {
    this.isDropdownOpen = true;
    this.positionDropdown();
  }

  /**
   * Handle dropdown close event.
   * Resets the dropdown position class.
   */
  private handleDropdownClose(): void {
    this.isDropdownOpen = false;
    this.resetDropdownPosition();
  }

  /**
   * Handle window resize event.
   * Repositions dropdown if it's currently open.
   */
  private handleWindowResize(): void {
    if (this.isDropdownOpen) {
      this.positionDropdown();
    }
  }

  /**
   * Position the dropdown above or below the input based on available viewport space.
   * This ensures the dropdown is fully visible without requiring scrolling.
   */
  private positionDropdown(): void {
    if (!this.itiContainer || !this.phoneNumberInput?.nativeElement) return;

    const inputRect = this.phoneNumberInput.nativeElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Calculate available space below and above the input
    const spaceBelow = viewportHeight - inputRect.bottom;
    const spaceAbove = inputRect.top;

    // If there's not enough space below but enough space above, position dropdown above
    if (spaceBelow < PhoneNumberInputComponent.DROPDOWN_HEIGHT_ESTIMATE && spaceAbove > spaceBelow) {
      this.itiContainer.classList.add("iti--dropdown-above");
    } else {
      this.itiContainer.classList.remove("iti--dropdown-above");
    }
  }

  /**
   * Reset dropdown position class when dropdown closes.
   */
  private resetDropdownPosition(): void {
    if (this.itiContainer) {
      this.itiContainer.classList.remove("iti--dropdown-above");
    }
  }

  onPhoneNumberChange(): void {
    const inputValue = this.phoneNumberInput.nativeElement.value;

    const sanitizedValue = inputValue.replace(/[^0-9\s+\-()]/g, "");

    if (inputValue !== sanitizedValue) {
      this.phoneNumberInput.nativeElement.value = sanitizedValue;
      this.contactNumberInput.setNumber(sanitizedValue);
    }

    if (sanitizedValue?.trim()) {
      this.isPhoneNumberInvalid = !this.contactNumberInput.isValidNumber();
    } else {
      this.isPhoneNumberInvalid = false;
    }

    this.validityChange.emit(this.isPhoneNumberInvalid);

    this._value = this.contactNumberInput.getNumber() ? this.contactNumberInput.getNumber().toString() : "";
    this.onChange(this._value);
    this.onTouched();
  }

  validatePhoneInput(event: KeyboardEvent): boolean {
    const allowedChars = /[0-9+\-() ]/;
    const key = event.key;

    if (
      event.ctrlKey ||
      event.metaKey ||
      key === "Backspace" ||
      key === "Delete" ||
      key === "ArrowLeft" ||
      key === "ArrowRight" ||
      key === "Tab"
    ) {
      return true;
    }

    if (!allowedChars.test(key)) {
      event.preventDefault();
      return false;
    }

    return true;
  }
}
